import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Paperclip, Loader2, CheckCircle2 } from 'lucide-react';
import { api } from '../../api/client';
import type { CheckoutPageData } from '@pay/shared';

function formatAmount(ore: number) {
  return (ore / 100).toLocaleString('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function Checkout() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<CheckoutPageData | null>(null);
  const [phone, setPhone] = useState('');
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function load() {
    if (!token) return;
    api
      .get<CheckoutPageData>(`/api/checkout/${token}`)
      .then((d) => {
        setData(d);
        setPhone(d.customer.phone ?? '');
      })
      .catch((e) => setError(e.message ?? 'Lenken er ugyldig eller utløpt.'));
  }
  useEffect(load, [token]);

  async function pay() {
    if (!token) return;
    setStarting(true);
    setError(null);
    try {
      const r = await api.post<{ redirect_url: string }>(`/api/checkout/${token}/start`, { phone });
      window.location.href = r.redirect_url;
    } catch (e) {
      setError((e as { message?: string }).message ?? 'Kunne ikke starte betalingen');
      setStarting(false);
    }
  }

  if (error && !data) {
    return (
      <div className="min-h-full flex items-center justify-center bg-slate-100">
        <div className="bg-white p-8 rounded-lg shadow max-w-md text-center">
          <div className="text-lg font-semibold mb-2">Beklager</div>
          <div className="text-slate-600">{error}</div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-full flex items-center justify-center">
        <Loader2 className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  const primary = data.owner.primary_color || '#1f2937';

  if (data.status === 'CAPTURED') {
    return (
      <div className="min-h-full bg-slate-100">
        <header className="px-6 py-5" style={{ background: primary }}>
          <div className="max-w-2xl mx-auto flex items-center gap-3">
            {data.owner.logo_url ? (
              <img src={data.owner.logo_url} alt={data.owner.name} className="h-12 max-w-[200px]" />
            ) : (
              <div className="text-white text-xl font-semibold">{data.owner.name}</div>
            )}
          </div>
        </header>
        <main className="max-w-2xl mx-auto p-6">
          <div className="bg-white rounded-lg shadow p-8 text-center">
            <CheckCircle2 size={56} className="mx-auto text-green-600 mb-4" />
            <h1 className="text-2xl font-semibold mb-2">Betaling fullført</h1>
            <p className="text-slate-600">
              {data.customer.email
                ? `Takk! Vi har sendt kvittering til ${data.customer.email}.`
                : 'Takk! Kvitteringen finner du i Vipps-appen.'}
            </p>
          </div>
        </main>
      </div>
    );
  }

  // Kravet kan ikke betales lenger (avbrutt, utløpt eller allerede godkjent): si det rett ut.
  const stengt =
    data.status === 'ABORTED' ? 'Betalingskravet er trukket tilbake. Ta kontakt med ' + data.owner.name + ' om du har spørsmål.'
    : data.status === 'EXPIRED' ? 'Lenken er utløpt. Ta kontakt med ' + data.owner.name + ' for en ny.'
    : data.status === 'AUTHORIZED' ? 'Betalingen er godkjent og behandles nå.'
    : data.status === 'REFUNDED' || data.status === 'PARTIALLY_REFUNDED' ? 'Betalingen er refundert.'
    : data.status === 'FAILED' ? 'Betalingen kunne ikke gjennomføres. Ta kontakt med ' + data.owner.name + '.'
    : null;

  return (
    <div className="min-h-full bg-slate-100">
      <header className="px-6 py-5" style={{ background: primary }}>
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          {data.owner.logo_url ? (
            <img src={data.owner.logo_url} alt={data.owner.name} className="h-12 max-w-[200px]" />
          ) : (
            <div className="text-white text-xl font-semibold">{data.owner.name}</div>
          )}
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-6 space-y-5">
        <section className="bg-white rounded-lg shadow p-6">
          <div className="text-slate-500 text-sm">Til betaling</div>
          <div className="text-4xl font-semibold mt-1 mb-1">{formatAmount(data.amount_ore)} kr</div>
          <div className="text-slate-700">{data.description}</div>

          <div className="mt-5 pt-5 border-t border-slate-200 text-sm space-y-1 text-slate-600">
            <div><span className="text-slate-500">Mottaker:</span> {data.owner.name}</div>
            <div><span className="text-slate-500">Til:</span> {data.customer.name}{data.customer.email ? ` (${data.customer.email})` : ''}</div>
          </div>
        </section>

        {data.attachments.length > 0 && (
          <section className="bg-white rounded-lg shadow p-6">
            <div className="font-medium mb-2 flex items-center gap-2">
              <Paperclip size={16} /> Vedlegg
            </div>
            <ul className="space-y-1">
              {data.attachments.map((a) => (
                <li key={a.id}>
                  <a href={a.download_url} target="_blank" rel="noopener" className="text-brand hover:underline text-sm">
                    {a.filename} <span className="text-slate-500">({Math.round(a.size_bytes / 1024)} kB)</span>
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}

        {stengt ? (
          <section className="bg-white rounded-lg shadow p-6 text-slate-700">{stengt}</section>
        ) : (
        <section className="bg-white rounded-lg shadow p-6 space-y-4">
          <label className="block">
            <span className="block text-sm font-medium text-slate-700 mb-1">Telefonnummer (for Vipps)</span>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Mobilnummer"
              inputMode="tel"
              className="w-full border border-slate-300 rounded px-3 py-2"
            />
          </label>

          {error && <div className="text-red-600 text-sm">{error}</div>}

          <button
            onClick={pay}
            disabled={starting}
            className="w-full rounded py-3 font-semibold text-white disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ background: '#ff5b24' }}
          >
            {starting ? <Loader2 size={18} className="animate-spin" /> : null}
            Betal med Vipps
          </button>
        </section>
        )}

        <div className="text-center text-xs text-slate-500">Betalingen håndteres trygt av Pay</div>
      </main>
    </div>
  );
}
